import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ConfigProvider } from 'antd';
import { ThemeProvider } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faUsers,
  faUserPlus,
  faUserCheck,
  faDollarSign,
  faChartLine,
  faSyncAlt,
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import {
  Container,
  HeaderSection,
  HeaderLeft,
  HeaderRight,
  TitleSection,
  MetricsGrid,
  ChartSection,
  ChartHeader,
  ChartControls,
  ChartMetrics,
  MetricItem,
  MetricLabel,
  MetricValue,
  ChartWrapper,
  FilterSection,
  FilterLabel,
} from './styles';
import InfoTile from './InfoTile';
import CustomersRankingGrid from './CustomersRankingGrid';
import RecentCustomersTable from './RecentCustomersTable';
import CustomersOrderTable from './CustomersOrderTable';
import { fetchCustomerSales, fetchCustomersWithOrders } from './store/customersSlice';
import { AppDispatch } from '../../../redux/store';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { useTheme } from '../../../context/ThemeContext';
import BarLineChart from '../../../commonComponents/BarLineChart';
import ChartSelection from '../../../commonComponents/ChartSelection';
import DateSelector from '../../../commonComponents/DateSelector';
import DateRangePicker from '../../../commonComponents/DateRangePicker';
import CustomersDropDown from '../../Loyalty/CustomersDropdown';
import StoresDropDown from '../StoresDropDown';
import {
  DEFAULT_DATE_RANGE,
  DEFAULT_PAGE,
  DEFAULT_LIMIT,
  CHART_TYPES,
  CHART_COLORS,
} from './constants';
import { DateRange, ChartData } from './types';

const Customers: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { customerData, customerOrderData, loading, orderLoading } = useSelector(
    (state: any) => state.customers
  );
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [chartType, setChartType] = useState<string>(CHART_TYPES.LINE);
  const [merchant, setMerchant] = useState<any>(null);
  const [storeId, setStoreId] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (user?.role === 'merchant' && !merchant) {
      setMerchant(user);
    }
  }, [user, merchant]);

  useEffect(() => {
    const fetchUserStores = async () => {
      if (user?.role !== 'admin' && !storeId) {
        try {
          const token = localStorage.getItem('access_token');
          const API = (await import('../../../context/API')).default;
          const res = await API.get('/stores', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          const stores = res.data.data || [];
          if (stores.length > 0) {
            setStoreId(stores[0].id);
          }
        } catch (error) {
          console.error('Error fetching user stores:', error);
        }
      }
    };

    fetchUserStores();
  }, [user, storeId]);

  useEffect(() => {
    const params = {
      startDate: moment(selectedDateRange.startDate).format('YYYY-MM-DD'),
      endDate: moment(selectedDateRange.endDate).format('YYYY-MM-DD'),
      merchantId: merchant?.id,
      store: storeId,
    };

    dispatch(fetchCustomerSales(params));
  }, [dispatch, selectedDateRange, merchant, storeId]);

  useEffect(() => {
    dispatch(
      fetchCustomersWithOrders({
        merchantId: merchant?.id,
        store: storeId,
        page: currentPage,
        limit: limit,
      })
    );
  }, [dispatch, merchant, storeId, currentPage, limit]);

  const handleDateSelection = (selection: DateRange) => {
    setSelectedDateRange(selection);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setSelectedDateRange(range);
  };

  const chartColors = isDarkMode ? CHART_COLORS.DARK : CHART_COLORS.LIGHT;

  const getChartData = (labels: string[], data: number[]): ChartData => ({
    labels,
    data,
    backgroundColor: [chartColors.PRIMARY, chartColors.SECONDARY],
    hoverBackgroundColor: [chartColors.HOVER_PRIMARY, chartColors.HOVER_SECONDARY],
  });

  const theme = {
    mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark',
  };

  const antdTheme = getAntdThemeConfig(isDarkMode);

  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider theme={antdTheme}>
        <Container>
          <HeaderSection>
            <HeaderLeft>
              <button
                onClick={() => navigate(-1)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} size="lg" />
              </button>
              <TitleSection>
                <FontAwesomeIcon icon={faUsers} size="lg" />
                <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>Customers</h1>
              </TitleSection>
            </HeaderLeft>
            <HeaderRight>
              {user.role === 'admin' && (
                <FilterSection>
                  <FilterLabel>By Merchants</FilterLabel>
                  <CustomersDropDown onOptionSelect={(merch) => setMerchant(merch)} />
                </FilterSection>
              )}
              <FilterSection>
                <FilterLabel>By Store</FilterLabel>
                <StoresDropDown
                  onOptionSelect={(store) => setStoreId(store.id)}
                  merchantId={merchant?.id}
                  role={user?.role}
                />
              </FilterSection>
            </HeaderRight>
          </HeaderSection>

          <MetricsGrid>
            <InfoTile
              title="Total Customers"
              subTitle="Total number of customers"
              value={customerData?.totalCustomers || 0}
              icon={faUsers}
              chartData={getChartData(['Total Customers'], [1])}
              loading={loading}
            />
            <InfoTile
              title="New Customers"
              subTitle="Customers who visited for the first time"
              value={customerData?.newCustomers || 0}
              icon={faUserPlus}
              chartData={getChartData(
                ['New', 'Others'],
                [
                  customerData?.newCustomers || 0,
                  (customerData?.totalCustomers || 0) - (customerData?.newCustomers || 0),
                ]
              )}
              loading={loading}
            />
            <InfoTile
              title="Repeat Customers"
              subTitle="Customers who have visited before"
              value={customerData?.repeatCustomers || 0}
              icon={faUserCheck}
              chartData={getChartData(
                ['Repeat', 'Others'],
                [
                  customerData?.repeatCustomers || 0,
                  (customerData?.totalCustomers || 0) - (customerData?.repeatCustomers || 0),
                ]
              )}
              loading={loading}
            />
            <InfoTile
              title="Average Spend per Customer"
              subTitle="Average amount spent per customer"
              value={`$${(customerData?.averageSpend || 0).toFixed(2)}`}
              icon={faDollarSign}
              chartData={getChartData(['Average'], [customerData?.averageSpend || 0])}
              loading={loading}
            />
            <InfoTile
              title="Average Visits per Customer"
              subTitle="Average number of visits per customer"
              value={customerData?.averageVisits || 0}
              icon={faChartLine}
              chartData={getChartData(
                ['Average'],
                [parseInt(String(customerData?.averageVisits)) || 0]
              )}
              loading={loading}
            />
          </MetricsGrid>

          <CustomersRankingGrid data={customerData?.topCustomers} loading={loading} />

          <ChartSection>
            <ChartHeader>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                Customer Visits Over Time
              </h2>
              <ChartControls>
                <DateRangePicker
                  selectedDateRange={selectedDateRange}
                  onDateRangeChange={handleDateRangeChange}
                />
                <DateSelector onDateSelect={handleDateSelection} />
                <ChartSelection onChange={(type) => setChartType(type)} />
              </ChartControls>
              <ChartMetrics>
                <MetricItem>
                  <FontAwesomeIcon icon={faDollarSign} color="#52c41a" />
                  <MetricLabel>Average Spend:</MetricLabel>
                  <MetricValue>${(customerData?.averageSpend || 0).toFixed(2)}</MetricValue>
                </MetricItem>
                <MetricItem>
                  <FontAwesomeIcon icon={faChartLine} color="#1890ff" />
                  <MetricLabel>Average Visits:</MetricLabel>
                  <MetricValue>{customerData?.averageVisits || 0}</MetricValue>
                </MetricItem>
                <MetricItem>
                  <FontAwesomeIcon icon={faSyncAlt} color="#fa8c16" />
                  <MetricLabel>Repeat Customers:</MetricLabel>
                  <MetricValue>{customerData?.repeatCustomers || 0}</MetricValue>
                </MetricItem>
                <MetricItem>
                  <FontAwesomeIcon icon={faUserPlus} color="#722ed1" />
                  <MetricLabel>New Customers:</MetricLabel>
                  <MetricValue>{customerData?.newCustomers || 0}</MetricValue>
                </MetricItem>
                <MetricItem>
                  <FontAwesomeIcon icon={faUsers} color="#722ed1" />
                  <MetricLabel>Total Customers:</MetricLabel>
                  <MetricValue>{customerData?.totalCustomers || 0}</MetricValue>
                </MetricItem>
              </ChartMetrics>
            </ChartHeader>
            <ChartWrapper>
              <BarLineChart
                data={customerData?.periodStats}
                chartType={chartType}
                xKey="period"
                yKey="numberOfCustomers"
                valueKey="numberOfCustomers"
                isLoading={loading}
              />
            </ChartWrapper>
          </ChartSection>

          <RecentCustomersTable data={customerData?.recentVisits} loading={loading} />

          <CustomersOrderTable
            customers={customerOrderData?.data}
            currentPage={currentPage}
            pageSize={limit}
            total={customerOrderData?.pagination?.totalItems || 0}
            onPageChange={(page) => setCurrentPage(page)}
            onPageSizeChange={(size) => setLimit(size)}
            loading={orderLoading}
          />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default Customers;
